import { loadGoogleMaps } from '../utils/google-maps-loader';

/**
 * Google Places (New) location autocomplete.
 *
 * Owns a plain text input and a custom suggestion dropdown, calling the
 * programmatic `AutocompleteSuggestion` API directly so the UI stays inside
 * our design system. A single `AutocompleteSessionToken` bundles every
 * keystroke with the final `fetchFields()` lookup into one billable session.
 *
 * On selection it serializes the full selection payload into a hidden input
 * and dispatches an identical object on a `location-selected` DOM event. The
 * payload carries the verbatim Google Place blob under `googlePlace`
 * alongside a few flattened convenience fields. The hidden input lets
 * server-rendered, DOM-as-state forms (the quote-request flow) submit the
 * selection with no JavaScript wiring. Clearing the field empties it and
 * dispatches `location-cleared`.
 *
 * A `selected` config value (the JSON a prior session stored) rehydrates the
 * hidden input on init, so a selection survives a form re-render.
 *
 * Place detail fields are fetched on selection. Each field tier carries a
 * cost, so this set is limited to what downstream consumers need to resolve
 * a city or address.
 */
const PLACE_FIELDS = [
    'id',
    'displayName',
    'formattedAddress',
    'location',
    'addressComponents',
    'types',
];

/** Prediction types that should render with a venue icon rather than a pin. */
const VENUE_TYPES = ['establishment', 'point_of_interest'];

/**
 * Split text into `{ text, isMatch }` parts around occurrences of the query.
 * The dropdown renders these with auto-escaped `x-text` bindings, so query
 * highlighting never needs `x-html` or manual HTML escaping.
 *
 * @param {string} text
 * @param {string} query
 * @returns {{ text: string, isMatch: boolean }[]}
 */
function splitOnQuery(text, query) {
    const trimmed = query.trim();

    if (!trimmed) {
        return [{ text, isMatch: false }];
    }

    const pattern = trimmed.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

    // String.split with a capturing group keeps the matched delimiters.
    return text
        .split(new RegExp(`(${pattern})`, 'ig'))
        .filter((segment) => segment !== '')
        .map((segment) => ({
            text: segment,
            isMatch: segment.toLowerCase() === trimmed.toLowerCase(),
        }));
}

export default function locationAutocomplete(config = {}) {
    // --- Configuration (supplied by the Blade component) ---
    const apiKey = config.apiKey ?? '';
    const includedPrimaryTypes = config.includedPrimaryTypes ?? [];
    const includedRegionCodes = config.includedRegionCodes ?? ['us'];
    const minChars = config.minChars ?? 2;
    const debounceMs = config.debounceMs ?? 250;
    const confirmSelection = config.confirmSelection ?? true;

    // --- Non-reactive internals ---
    // Google's PlacePrediction and session-token objects are class instances;
    // keeping them out of Alpine's reactivity proxy avoids subtle breakage of
    // their methods (e.g. `prediction.toPlace()`).
    let rawPredictions = [];
    let placesLibrary = null;
    let sessionToken = null;
    let debouncedFetch = null;
    let selectedLabel = '';
    // Depth counter, not a boolean: onBlur() and an in-flight selectSuggestion()
    // can both suppress the watcher at once and each releases on its own
    // $nextTick. A counter lets each release undo only its own suppression, so
    // the later programmatic query mutation stays guarded regardless of order.
    let suppressDepth = 0;
    let requestId = 0;

    return {
        // --- Reactive state (bound in the template) ---
        query: config.value ?? '',
        // JSON-serialized selection payload, mirrored into the hidden input.
        // Empty string means nothing is selected.
        placeData: '',
        suggestions: [],
        highlightedIndex: -1,
        showResults: false,
        loading: false,
        // Inline message shown when Google Maps fails to load or a picked
        // suggestion can't be resolved.
        error: '',

        // --- Derived indicator state ---
        // Only one icon occupies the input's right slot at a time: the spinner
        // while a request is in flight, otherwise a confirmation check when a
        // selection exists and `confirmSelection` is enabled. `hasIndicator`
        // drives the input's right padding, so an empty field uses full width.
        get showSpinner() {
            return this.loading;
        },

        get showCheck() {
            return confirmSelection && !this.loading && this.placeData !== '';
        },

        get hasIndicator() {
            return this.showSpinner || this.showCheck;
        },

        init() {
            debouncedFetch = Alpine.debounce(this.fetchSuggestions.bind(this), debounceMs);

            // Restore a server-supplied selection (e.g. a re-rendered form):
            // mirror its JSON into the hidden input and treat the prefilled
            // query as the committed label, so edit-tracking and blur-restore
            // behave as if the user had just picked it. Done before the
            // watcher is registered so it does not trigger a suggestion fetch.
            if (config.selected) {
                this.placeData = config.selected;
                selectedLabel = this.query;
            }

            this.$watch('query', this.handleQueryChange.bind(this));
        },

        handleQueryChange(value) {
            if (suppressDepth > 0) {
                return;
            }

            // Typing dismisses any error left from a failed selection.
            this.error = '';

            // Editing the text after a selection invalidates that selection.
            if (this.placeData && value !== selectedLabel) {
                this.clearSelection();
            }

            const trimmed = value.trim();

            if (trimmed.length < minChars) {
                this.resetSuggestions();
                this.loading = false;
                return;
            }

            // Show the loading state immediately; the request itself is debounced.
            this.loading = true;
            debouncedFetch(trimmed);
        },

        onFocus() {
            if (this.suggestions.length > 0) {
                this.showResults = true;
            }
        },

        onBlur() {
            // Restore the full label of the active selection if the user left
            // partial text in the field.
            if (selectedLabel && this.query !== selectedLabel) {
                suppressDepth++;
                this.query = selectedLabel;
                this.$nextTick(() => {
                    suppressDepth--;
                });
            }
        },

        async fetchSuggestions(query) {
            const currentRequest = ++requestId;
            this.loading = true;

            let AutocompleteSuggestion;
            let AutocompleteSessionToken;
            try {
                ({ AutocompleteSuggestion, AutocompleteSessionToken } = await this.ensurePlacesLibrary());
            } catch (error) {
                // Log before the race guard so a load failure superseded by a
                // newer keystroke still leaves a diagnostic behind.
                console.error('Google Maps failed to load:', error);
                if (currentRequest !== requestId) {
                    return;
                }
                this.resetSuggestions();
                this.error = 'Google Maps failed to load. Try refreshing the page.';
                this.loading = false;
                return;
            }

            try {
                // A session spans every keystroke up to the next selection.
                if (!sessionToken) {
                    sessionToken = new AutocompleteSessionToken();
                }

                const request = {
                    input: query,
                    sessionToken,
                    includedRegionCodes,
                    language: document.documentElement.lang || 'en',
                };

                if (includedPrimaryTypes.length > 0) {
                    request.includedPrimaryTypes = includedPrimaryTypes;
                }

                const { suggestions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

                // Drop responses superseded by a newer keystroke.
                if (currentRequest !== requestId) {
                    return;
                }

                rawPredictions = suggestions.map((suggestion) => suggestion.placePrediction).filter((prediction) => prediction != null);

                this.suggestions = rawPredictions.map((prediction, index) => this.normalizePrediction(prediction, index, query));
                this.highlightedIndex = -1;
                this.showResults = this.suggestions.length > 0;
            } catch (error) {
                if (currentRequest !== requestId) {
                    return;
                }
                console.error('Location autocomplete request failed:', error);
                this.resetSuggestions();
            } finally {
                if (currentRequest === requestId) {
                    this.loading = false;
                }
            }
        },

        normalizePrediction(prediction, index, query) {
            const types = prediction.types ?? [];
            const mainText = prediction.mainText?.toString() ?? prediction.text?.toString() ?? '';
            const secondaryText = prediction.secondaryText?.toString() ?? '';

            return {
                index,
                placeId: prediction.placeId,
                mainText,
                mainParts: splitOnQuery(mainText, query),
                secondaryParts: splitOnQuery(secondaryText, query),
                hasSecondaryText: secondaryText !== '',
                isVenue: types.some((type) => VENUE_TYPES.includes(type)),
            };
        },

        async selectSuggestion(index) {
            const prediction = rawPredictions[index];
            if (!prediction) {
                return;
            }

            suppressDepth++;
            this.loading = true;
            this.error = '';

            try {
                const place = prediction.toPlace();
                await place.fetchFields({ fields: PLACE_FIELDS });

                const label = place.formattedAddress || place.displayName || this.suggestions[index]?.mainText || '';

                this.query = label;
                selectedLabel = label;
                this.applySelection(place);
                this.closeResults();

                // fetchFields() closes the billing session. The next
                // keystroke starts a fresh one.
                sessionToken = null;
            } catch (error) {
                console.error('Failed to load Google Place details:', error);
                this.error = 'Sorry, we couldn’t load that location. Please pick it again.';
                // Drop any prior selection so the hidden input can't submit a
                // location that no longer matches what the field shows.
                this.clearSelection();
            } finally {
                this.loading = false;
                this.$nextTick(() => {
                    suppressDepth--;
                });
            }
        },

        highlightNext() {
            if (this.suggestions.length === 0) {
                return;
            }
            this.showResults = true;
            this.highlightedIndex = (this.highlightedIndex + 1) % this.suggestions.length;
        },

        highlightPrevious() {
            if (this.suggestions.length === 0) {
                return;
            }
            this.showResults = true;
            this.highlightedIndex = this.highlightedIndex <= 0 ? this.suggestions.length - 1 : this.highlightedIndex - 1;
        },

        onEnter() {
            if (this.showResults && this.highlightedIndex >= 0) {
                this.selectSuggestion(this.highlightedIndex);
            }
        },

        closeResults() {
            this.showResults = false;
            this.highlightedIndex = -1;
        },

        resetSuggestions() {
            rawPredictions = [];
            this.suggestions = [];
            this.highlightedIndex = -1;
            this.showResults = false;
        },

        clearSelection() {
            this.placeData = '';
            selectedLabel = '';
            this.$dispatch('location-cleared');
        },

        /**
         * Commit a resolved Google Place. The normalized payload is mirrored
         * into the hidden input as JSON. The quote-request flow keeps its
         * state in the DOM, so the selection must live there. The same
         * object is announced on a `location-selected` event.
         *
         * @param {google.maps.places.Place} place
         */
        applySelection(place) {
            const payload = this.normalizeGooglePlace(place);

            this.placeData = JSON.stringify(payload);
            this.$dispatch('location-selected', payload);
        },

        /**
         * Build the `location-selected` payload from a resolved Google Place.
         *
         * `googlePlace` is the verbatim Google blob: every field fetched in
         * PLACE_FIELDS. It's the canonical structure to persist or send to
         * the back end. The sibling fields are flattened convenience accessors
         * for the common cases; anything else (e.g. `addressComponents`) is
         * still reachable through `googlePlace`. This is the single place that
         * touches Google's place shape.
         *
         * @param {google.maps.places.Place} place
         */
        normalizeGooglePlace(place) {
            // Place.toJSON() collapses Google's class instances (LatLng,
            // AddressComponent, ...) into plain, JSON-serializable values.
            const googlePlace = place.toJSON();

            return {
                placeId: googlePlace.id ?? null,
                displayName: googlePlace.displayName ?? null,
                formattedAddress: googlePlace.formattedAddress ?? null,
                coordinates: googlePlace.location ?? null,
                types: googlePlace.types ?? [],
                googlePlace,
            };
        },

        async ensurePlacesLibrary() {
            if (placesLibrary) {
                return placesLibrary;
            }

            const maps = await loadGoogleMaps({ apiKey });
            const { AutocompleteSuggestion, AutocompleteSessionToken } = await maps.importLibrary('places');
            placesLibrary = { AutocompleteSuggestion, AutocompleteSessionToken };

            return placesLibrary;
        },
    };
}

document.addEventListener('alpine:init', () => {
    Alpine.data('locationAutocomplete', locationAutocomplete);
});
